import { useCallback, useMemo, useState } from "react";
import { omitUndefined, toastManager } from "@salestrak/ui";

import type { IssueRow, StageOption } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useIssueTypesWithFields } from "#app/hooks/issue-view/use-issue-types-with-fields";
import { useMigrateIssueType } from "#app/hooks/issue-view/use-migrate-issue-type";
import {
  ISSUE_STAGE_COLUMN_ID,
  ISSUE_TYPE_COLUMN_ID,
  ISSUE_WORKFLOW_COLUMN_ID,
  useIssueColumns,
  type UseIssueColumnsParams,
} from "#app/components/issues/issue-columns";
import {
  issueCustomFieldColumnId,
  useIssueCustomFieldColumns,
} from "#app/components/issues/issue-custom-field-columns";
import { useCustomFieldColumnMenuItems } from "#app/components/custom-fields/custom-field-column-menu";
import { useCustomFieldValueCommit } from "#app/hooks/issue-view/use-custom-field-value-commit";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  TABLE_CELL_EDIT,
  useIssueViewMutations,
} from "#app/hooks/issue-view/use-issue-view-mutations";
import { useIssueTeamsChange } from "#app/components/issues/use-issue-teams-change";
import { useViewColumnState } from "#app/lib/hooks/use-view-column-state";
import type { ViewColumns, ViewConfig } from "#app/lib/view-config";

/**
 * The issue list's column roster and the cell actions that feed it: the
 * shared issue columns plus every workspace custom field as an opt-in column,
 * the per-cell mutations (teams, address) and the two dialogs a cell can open
 * (the age update, the type migration), and the column state split between
 * the view draft and the per-user pool.
 */
export function useIssuesTableColumns({
  workflowId,
  availableStages,
  handleOpenIssue,
  changeStage,
  updateAssignees,
  moveToWorkflow,
  activeViewId,
  viewsLoading,
  viewColumns,
  setConfig,
}: {
  workflowId: string | undefined;
  availableStages: StageOption[];
  handleOpenIssue: (issue: IssueRow) => void;
  changeStage: UseIssueColumnsParams["onChangeStage"];
  updateAssignees: UseIssueColumnsParams["onAssigneesChange"];
  moveToWorkflow: (issueId: string, workflowId: string, stageId?: string) => void;
  activeViewId: string | null;
  viewsLoading: boolean;
  viewColumns: ViewConfig["columns"];
  setConfig: (patch: Partial<ViewConfig>) => void;
}) {
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAgePress = useCallback((issue: IssueRow) => setAgeDialogIssue(issue), []);
  const handleAgeDialogClose = useCallback((open: boolean) => {
    if (!open) setAgeDialogIssue(null);
  }, []);

  // Type-column picks stage a migration; the dialog collects the target
  // type's required-field values before the move commits.
  const issueTypesWithFields = useIssueTypesWithFields();
  const migrateIssueType = useMigrateIssueType();
  const [typeMigration, setTypeMigration] = useState<{
    issue: IssueRow;
    issueTypeId: string;
  } | null>(null);
  const handleIssueTypeChange = useCallback(
    (issue: IssueRow, issueTypeId: string) => {
      if (issueTypeId === issue.issueTypeId) return;
      setTypeMigration({ issue, issueTypeId });
    },
    [],
  );
  const handleTypeMigrationConfirm = useCallback(
    async (issueTypeId: string, values: Record<string, unknown>) => {
      if (!typeMigration) return;
      await migrateIssueType(typeMigration.issue.issueId, issueTypeId, values, "table_cell");
      setTypeMigration(null);
    },
    [typeMigration, migrateIssueType],
  );

  const { updateAddress } = useIssueViewMutations(TABLE_CELL_EDIT);
  const handleTeamsChange = useIssueTeamsChange(TABLE_CELL_EDIT);

  const handleAddressSelect = useCallback(
    (issue: IssueRow, wholeAddress: string) => {
      updateAddress({
        variables: { issueId: issue.issueId, address: { wholeAddress } },
        optimisticResponse: {
          // `omitUndefined` because the optimistic payload must match the
          // schema shape: an issue with no address DELETES `addressId` rather
          // than caching an explicit `undefined`.
          updateIssue: omitUndefined({
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: issue.addressId,
            address: {
              __typename: "Address" as const,
              id: issue.address?.id ?? `optimistic-${issue.issueId}`,
              addressId: issue.address?.addressId ?? `optimistic-${issue.issueId}`,
              wholeAddress,
            },
          }),
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename !== "Issue") {
            toastManager.add({ title: "message" in result ? result.message : "Failed to update address", type: "error" });
          }
        },
        onError: (err) => toastMutationError(err, "Failed to update address"),
      });
    },
    [updateAddress],
  );

  const handleAddressClear = useCallback(
    (issue: IssueRow) => {
      updateAddress({
        variables: { issueId: issue.issueId, address: null },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: null,
            address: null,
          },
        },
        onCompleted: (data) => {
          const result = data.updateIssue;
          if (result.__typename !== "Issue") {
            toastManager.add({ title: "message" in result ? result.message : "Failed to clear address", type: "error" });
          }
        },
        onError: (err) => toastMutationError(err, "Failed to clear address"),
      });
    },
    [updateAddress],
  );

  const handleWorkflowChange = useCallback(
    (issue: IssueRow, targetWorkflowId: string, stageId: string) =>
      moveToWorkflow(issue.issueId, targetWorkflowId, stageId),
    [moveToWorkflow],
  );

  // --- Columns ---
  const rawColumns = useIssueColumns({
    availableStages,
    onOpenIssue: handleOpenIssue,
    onChangeStage: changeStage,
    onAssigneesChange: updateAssignees,
    onTeamsChange: handleTeamsChange,
    onAgePress: handleAgePress,
    onIssueTypeChange: handleIssueTypeChange,
    onWorkflowChange: handleWorkflowChange,
    onAddressSelect: handleAddressSelect,
    onAddressClear: handleAddressClear,
    // This host provides IssueRenameControls, so the name cells carry the
    // hover Rename action alongside the row menu's Rename.
    renameAction: true,
  });

  // --- Custom-field columns ---
  // Every workspace field is an opt-in column; cells reuse the issue view
  // dialog's value cell. Hidden by default via `defaultHiddenColumnIds` below.
  const { customFields } = useWorkspaceData();
  const commitCustomFieldValue = useCustomFieldValueCommit({ tracking: TABLE_CELL_EDIT });
  const customFieldColumns = useIssueCustomFieldColumns({
    fields: customFields,
    commit: commitCustomFieldValue,
  });
  const allColumns = useMemo(
    () => [...rawColumns, ...customFieldColumns],
    [rawColumns, customFieldColumns],
  );
  // Hidden until shown from the Fields panel: every custom field, the Stage
  // column — the name cell's glyph already carries the stage — the Type
  // column, a second reading of the name cell's icon, and inside ONE
  // workflow the Workflow column, which would only repeat the page's name.
  const defaultHiddenColumnIds = useMemo(
    () => [
      ISSUE_STAGE_COLUMN_ID,
      ISSUE_TYPE_COLUMN_ID,
      ...(workflowId ? [ISSUE_WORKFLOW_COLUMN_ID] : []),
      ...customFields.map((f) => issueCustomFieldColumnId(f.customFieldId)),
    ],
    [customFields, workflowId],
  );
  // Header context menu: custom-field columns carry "Edit field" and (inside
  // a single-workflow scope) "Automate field".
  const renderCustomFieldMenuItems = useCustomFieldColumnMenuItems({
    fields: customFields,
    workflowId,
  });

  // --- Column state: the view's inside a saved view, per-user outside ---
  // `useViewColumnState` owns the split: inside a saved view the column set
  // rides the view draft — the panel's Fields page, the header "+" and
  // in-table column drags all raise Save — while outside one (the team and
  // member lists) the per-user pool owns everything. Widths stay per-user
  // either way: a reading ergonomic, not a view decision.
  const handleViewColumnsChange = useCallback(
    (columns: ViewColumns) => setConfig({ columns }),
    [setConfig],
  );
  const columnState = useViewColumnState({
    tableId: "issues",
    defaultHiddenColumnIds,
    activeViewId,
    viewsLoading,
    columns: viewColumns,
    onColumnsChange: handleViewColumnsChange,
  });

  return {
    allColumns,
    customFields,
    renderCustomFieldMenuItems,
    columnState,
    ageDialogIssue,
    handleAgeDialogClose,
    typeMigration,
    setTypeMigration,
    issueTypesWithFields,
    handleTypeMigrationConfirm,
  };
}
